<script setup>
import { ref, computed } from 'vue'
import { requiredValidator } from '../../../utils/validators'
import { useCmsContentStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'

const props = defineProps({
  isDialogVisible: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:isDialogVisible', 'saved'])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth(700)

const cmsStore = useCmsContentStore()
const form = ref()
const saving = ref(false)

const initialData = {
  key: '',
  value: '',
  contentType: 'text',
  blockSchema: null,
  displayName: '',
  description: '',
  category: 'marketing',
}

const newItem = ref(structuredClone(initialData))

const categories = [
  { title: 'Marketing', value: 'marketing' },
  { title: 'Navigation', value: 'navigation' },
  { title: 'Form', value: 'form' },
  { title: 'Action', value: 'action' },
  { title: 'Validation', value: 'validation' },
  { title: 'Filter', value: 'filter' },
  { title: 'Dialog', value: 'dialog' },
  { title: 'Empty State', value: 'empty-state' },
  { title: 'Help', value: 'help' },
  { title: 'Testimonial', value: 'testimonial' },
]

const contentTypes = [
  { title: 'Text', value: 'text' },
  { title: 'Rich Text', value: 'richtext' },
  { title: 'Block (Structured)', value: 'block' },
]

// Tiptap config for richtext
const tiptapOutput = ref('html')
const tiptapMarkdownTheme = ref('')
const tiptapOutlined = ref(true)
const tiptapMaxWidth = ref(900)

const isBlock = computed(() => newItem.value.contentType === 'block')
const isRichText = computed(() => newItem.value.contentType === 'richtext')
const isText = computed(() => !newItem.value.contentType || newItem.value.contentType === 'text')

// Block schema builder
const blockSchemaFields = ref([])

const addSchemaField = () => {
  blockSchemaFields.value.push({ field: '', label: '', type: 'text', required: false })
}

const removeSchemaField = index => {
  blockSchemaFields.value.splice(index, 1)
}

const keyValidator = value => {
  if (!value) return 'Key is required'
  if (!/^[a-zA-Z0-9._-]+$/.test(value)) return 'Key can only contain letters, numbers, dots, hyphens, and underscores'
  
  return true
}

const updateModelValue = val => {
  emit('update:isDialogVisible', val)
}

const onFormSubmit = async () => {
  const { valid: isValid } = await form.value.validate()
  if (!isValid) return

  try {
    saving.value = true

    const data = { ...newItem.value }

    // For block type, set up the schema and initial empty array
    if (data.contentType === 'block') {
      data.blockSchema = blockSchemaFields.value.filter(f => f.field)
      if (!Array.isArray(data.value) || data.value.length === 0) {
        data.value = []
      }
    }

    await cmsStore.createContentItem(data)
    emit('saved')
    newItem.value = structuredClone(initialData)
    blockSchemaFields.value = []
    emit('update:isDialogVisible', false)
  } catch (error) {
    console.error('Error creating CMS content:', error)
  } finally {
    saving.value = false
  }
}

const resetAndClose = () => {
  newItem.value = structuredClone(initialData)
  blockSchemaFields.value = []
  emit('update:isDialogVisible', false)
}
</script>

<template>
  <VDialog
    :model-value="isDialogVisible"
    :width="dWidth"
    @update:model-value="updateModelValue"
  >
    <VCard class="pa-sm-9 pa-5">
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          New Content Item
        </VCardTitle>
      </VCardItem>

      <VCardText>
        <VForm
          ref="form"
          class="mt-4"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <!-- Key -->
            <VCol cols="12">
              <VTextField
                v-model="newItem.key"
                label="Key"
                :rules="[keyValidator]"
                hint="Dot-notation path, e.g. home.masthead.headline"
                persistent-hint
                placeholder="section.subsection.name"
              />
            </VCol>

            <!-- Display Name -->
            <VCol cols="12">
              <VTextField
                v-model="newItem.displayName"
                label="Display Name"
                hint="Human-readable name"
                persistent-hint
              />
            </VCol>

            <!-- Description -->
            <VCol cols="12">
              <VTextField
                v-model="newItem.description"
                label="Description"
                hint="Where/how this content appears"
                persistent-hint
              />
            </VCol>

            <!-- Category & Content Type -->
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="newItem.category"
                label="Category"
                :items="categories"
                :rules="[requiredValidator]"
              />
            </VCol>
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="newItem.contentType"
                label="Content Type"
                :items="contentTypes"
              />
            </VCol>

            <!-- Text Value -->
            <VCol
              v-if="isText"
              cols="12"
            >
              <VTextarea
                v-model="newItem.value"
                label="Value"
                :rules="[requiredValidator]"
                rows="3"
                auto-grow
              />
            </VCol>

            <!-- Rich Text Value -->
            <VCol
              v-if="isRichText"
              cols="12"
            >
              <div class="text-body-2 mb-1">
                Value (Rich Text)
              </div>
              <VuetifyTiptap
                v-model="newItem.value"
                v-model:markdown-theme="tiptapMarkdownTheme"
                label="Content"
                :output="tiptapOutput"
                :outlined="tiptapOutlined"
                rounded
                :max-height="400"
                :max-width="tiptapMaxWidth"
              />
            </VCol>

            <!-- Block: Schema Builder -->
            <VCol
              v-if="isBlock"
              cols="12"
            >
              <div class="d-flex align-center mb-3">
                <div class="text-body-2 font-weight-medium">
                  Block Schema (define fields for each item)
                </div>
                <VSpacer />
                <VBtn
                  size="small"
                  variant="outlined"
                  @click="addSchemaField"
                >
                  Add Field
                </VBtn>
              </div>

              <VCard
                v-for="(fieldDef, fieldIndex) in blockSchemaFields"
                :key="fieldIndex"
                class="mb-2 pa-3"
                variant="outlined"
              >
                <VRow dense>
                  <VCol
                    cols="12"
                    sm="3"
                  >
                    <VTextField
                      v-model="fieldDef.field"
                      label="Field Key"
                      density="compact"
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="3"
                  >
                    <VTextField
                      v-model="fieldDef.label"
                      label="Label"
                      density="compact"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="3"
                  >
                    <VSelect
                      v-model="fieldDef.type"
                      label="Type"
                      density="compact"
                      :items="[
                        { title: 'Text', value: 'text' },
                        { title: 'Textarea', value: 'textarea' },
                      ]"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="2"
                  >
                    <VCheckbox
                      v-model="fieldDef.required"
                      label="Required"
                      density="compact"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    sm="1"
                    class="d-flex align-center"
                  >
                    <VBtn
                      icon="mdi-delete-outline"
                      size="x-small"
                      variant="text"
                      color="error"
                      @click="removeSchemaField(fieldIndex)"
                    />
                  </VCol>
                </VRow>
              </VCard>

              <div
                v-if="blockSchemaFields.length === 0"
                class="text-caption text-disabled"
              >
                No fields defined yet. Add fields to define the structure of each block item.
              </div>
            </VCol>

            <!-- Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                :loading="saving"
                @click="onFormSubmit"
              >
                Create
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="resetAndClose"
              >
                Cancel
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>
